import {
  JepxSpotPortion,
  PlanSourceType,
} from '@ebalance/proto/bfftypes/v1/plan_submission_pb'

import { PLAN_SOURCE_TYPE_I18N_KEYS } from '~/pages/ProcurementPlanDetail/constants'
import {
  type ColumnKey,
  type SlotValue,
  calcColumnDiff,
  isDiffCell,
  makeColumnKey,
} from '~/pages/ProcurementPlanDetail/models/calc'
import { isJointBg } from '~/pages/ProcurementPlanDetail/models/jointBg'

/** 調達 / 販売の側 */
export type ColumnSide = 'procurement' | 'sales'

/** 列キー解決に必要な最小フィールド（proto 行と構造的に互換。optional フィールド） */
export type ColumnRowLike = {
  sourceType: PlanSourceType
  counterpartyCode?: string
  /**
   * JEPX スポット調達の 相対契約分 / 調達先未定分 を振り分ける型付き信号。worker が付与し
   * backend converter が proto enum に載せる（counterparty_name 文字列照合に依存しない）。
   */
  jepxSpotPortion?: JepxSpotPortion
  /**
   * 契約識別子（UUID、trading_contract.id）。相対契約行に存在（0 埋め継承行も
   * 前世代の source_contract_id を保持するため独立列として残る）、JEPX 市場行
   * では不在（proto3 optional）。「取引毎」表示モードの列分割キー
   * （spec procurement-plan.md L67 / plan-common.md L343-344、issue #5068。
   * #6236 で旧 source_leg_id から rename）。
   */
  sourceContractId?: string
}

/** 列を組み立てる入力行（計画値行。proto ProcurementPlanValueRow と構造的に互換） */
export type BreakdownSourceRow = ColumnRowLike & {
  counterpartyName?: string
}

/**
 * テーブルに表示する内訳列 1 列分の表示情報（index.tsx が組み立て、PlanTable が描画する
 * 表示行の契約）。JEPX 市場列と取引先列の両方をこの型で表す。
 */
export type BreakdownColumn = {
  /** 列のキー（一意識別子。編集バッファのキーでもある） */
  key: string
  /** 取引先名（取引先列の 2 段目）。市場列では undefined */
  counterpartyName: string | undefined
  /** 取引先コード（取引先列の 1 段目）。市場列では undefined */
  counterpartyCode: string | undefined
  /** 契約識別子（列同定用。ヘッダには表示しない）。市場列では undefined */
  sourceContractId: string | undefined
  /** sourceType の日本語ラベル（counterpartyName が無い場合のフォールバック） */
  sourceLabel: string
  /**
   * JEPX 市場列のヘッダラベル。設定時は取引先 2 段ヘッダではなく
   * code（JSPT3 / J1HR3）+ primary（前日スポット / 時間前）+ secondary（(相対契約分) 等）
   * の固定ラベルを表示する。
   */
  fixedLabel?: { code: string; primary: string; secondary: string | undefined }
  /** 48 スロットの編集値 */
  slots: SlotValue[]
  /** コマ単位の前回差分フラグ */
  diffFlags: boolean[]
  /**
   * 前回提出の 48 スロット値（hover tooltip 用、plan-diff-cell-tooltip 設計書 Decision 5）。
   * 前回提出が無い場合だけ列全体が undefined。前回提出はあるが対応列が無い列は
   * 前回値 0 の 48 スロット（AC-PLC-013: 列が無いことと 0 は計画上同じ意味）。
   */
  previousSlots?: SlotValue[]
}

/**
 * JEPX 市場列 1 列分の仕様。
 * `key` は列の安定 ID であり、編集バッファ（slotsMap）のキーにもなる。
 */
export type JepxMarketSpec = {
  key: string
  /**
   * OCCTO の取引相手コード（ヘッダ最上段に表示）。JEPX スポット = JSPT3、
   * JEPX 時間前 = J1HR3。制度上固定の安定コードのため i18n しない。
   */
  code: string
  /** ヘッダ中段の i18n キー（market.jepxSpot=前日スポット / market.jepxIntraday=時間前） */
  primaryKey: string
  /** ヘッダ下段の i18n キー（(相対契約分) / (調達先未定分)）。無ければ省略 */
  secondaryKey?: string
  /**
   * この列を出すトリガーとなる値行の sourceType。マスタにこの市場の取引先が
   * 登録され、worker が当該 sourceType の値行を生成した時だけ列を出す（= 常時固定
   * 表示はしない）。JEPX アカウントを持たない事業者は codes[] にこの市場を入れない
   * ため列が出ず、広域(OCCTO)で JEPX を必須化して提出不能になる事故を避ける。
   */
  sourceType: PlanSourceType
}

/** JEPX スポット市場の OCCTO 取引相手コード */
const JEPX_SPOT_CODE = 'JSPT3'
/** JEPX 時間前市場の OCCTO 取引相手コード */
const JEPX_INTRADAY_CODE = 'J1HR3'

/**
 * JEPX 市場列の安定キー。resolveColumnKey（行 → 列キー）と JEPX_MARKET_SPECS（列ヘッダ）
 * が同じ語彙を共有することで、二重定義のドリフト（= JEPX 行が取引先列に漏れる silent bug）
 * を防ぐ。
 */
const JEPX_COLUMN_KEY = {
  spotBilateral: 'jepx-spot-bilateral',
  spotUndetermined: 'jepx-spot-undetermined',
  spotSales: 'jepx-spot',
  intraday: 'jepx-intraday',
} as const

const marketKey = (k: string): string =>
  `page:procurementPlanDetail.market.${k}`

/**
 * JEPX 市場列の仕様（先頭・canonical 順）。値行に対応 sourceType が存在する時だけ
 * `buildBreakdownColumns` が出力する（マスタ取引先駆動。常時固定表示はしない）。
 *
 * - 調達: 前日スポット(相対契約分) → 前日スポット(調達先未定分) → 時間前
 *   （調達側スポットは構造的に 2 列。domain-knowledge.md §M16 JP06367 の 2 区分）
 * - 販売: 前日スポット → 時間前（スポットは分割しない）
 *
 * 沖縄エリアは JEPX 対象外だが、その表現はマスタに JEPX 取引先を登録しないことで行う
 * （= 列は値行の sourceType 有無で決まり、表示層に沖縄特例を持たない。旧
 * submission-management.md L217「JEPX 必ず表示」は不備で撤回し、procurement-plan.md
 * L310「列構成は要確認」をマスタ取引先駆動で確定）。
 */
export const JEPX_MARKET_SPECS: Record<ColumnSide, JepxMarketSpec[]> = {
  procurement: [
    {
      key: JEPX_COLUMN_KEY.spotBilateral,
      code: JEPX_SPOT_CODE,
      primaryKey: marketKey('jepxSpot'),
      secondaryKey: marketKey('bilateralPortion'),
      sourceType: PlanSourceType.JEPX_SPOT,
    },
    {
      key: JEPX_COLUMN_KEY.spotUndetermined,
      code: JEPX_SPOT_CODE,
      primaryKey: marketKey('jepxSpot'),
      secondaryKey: marketKey('undeterminedPortion'),
      sourceType: PlanSourceType.JEPX_SPOT,
    },
    {
      key: JEPX_COLUMN_KEY.intraday,
      code: JEPX_INTRADAY_CODE,
      primaryKey: marketKey('jepxIntraday'),
      sourceType: PlanSourceType.JEPX_INTRADAY,
    },
  ],
  sales: [
    {
      key: JEPX_COLUMN_KEY.spotSales,
      code: JEPX_SPOT_CODE,
      primaryKey: marketKey('jepxSpot'),
      sourceType: PlanSourceType.JEPX_SPOT,
    },
    {
      key: JEPX_COLUMN_KEY.intraday,
      code: JEPX_INTRADAY_CODE,
      primaryKey: marketKey('jepxIntraday'),
      sourceType: PlanSourceType.JEPX_INTRADAY,
    },
  ],
}

const JEPX_KEY_SET: Record<ColumnSide, ReadonlySet<string>> = {
  procurement: new Set(JEPX_MARKET_SPECS.procurement.map((s) => s.key)),
  sales: new Set(JEPX_MARKET_SPECS.sales.map((s) => s.key)),
}

/** 与えられた列キーが JEPX 市場列のものか */
export function isJepxMarketKey(key: string, side: ColumnSide): boolean {
  return JEPX_KEY_SET[side].has(key)
}

/**
 * 計画値行が属する列キーを解決する。
 *
 * - JEPX スポット: 調達は jepxSpotPortion(型付き enum) で 相対契約分 / 調達先未定分 に
 *   振り分ける（worker は 2 行とも counterparty_code=JSPT3 で出し、portion で区別する。
 *   domain-knowledge.md §M16 JP06367 の 2 区分）。販売は分割せず 1 列。
 * - JEPX 時間前: 1 列。
 * - それ以外（BG 相対 / eClear / 常時BU 等）: 取引先別の makeColumnKey。
 */
export function resolveColumnKey(row: ColumnRowLike, side: ColumnSide): string {
  if (row.sourceType === PlanSourceType.JEPX_SPOT) {
    if (side === 'sales') return JEPX_COLUMN_KEY.spotSales
    // portion=BILATERAL → 相対契約分列 / それ以外（UNDETERMINED / UNSPECIFIED）→ 調達先未定分列
    return row.jepxSpotPortion === JepxSpotPortion.BILATERAL
      ? JEPX_COLUMN_KEY.spotBilateral
      : JEPX_COLUMN_KEY.spotUndetermined
  }
  if (row.sourceType === PlanSourceType.JEPX_INTRADAY) {
    return JEPX_COLUMN_KEY.intraday
  }
  const key: ColumnKey = {
    sourceType: row.sourceType,
    counterpartyCode: row.counterpartyCode,
    sourceContractId: row.sourceContractId,
  }
  return makeColumnKey(key)
}

/**
 * 同一列キーに複数行が来た場合のスロット合算。
 * （相対契約分に複数取引先がある等の希少ケース。1 列に集約する）
 * 両方未入力なら未入力、片方でも数値があれば数値同士を合算（未入力は 0 扱い）。
 */
export function mergeSlots(
  into: SlotValue[] | undefined,
  add: SlotValue[],
): SlotValue[] {
  if (into === undefined) return [...add]
  return into.map((v, i) => {
    const w = add[i]
    if (v === undefined && w === undefined) return undefined
    return (v ?? 0) + (w ?? 0)
  })
}

// 値行が 1 つも無い内訳列（例: JEPX 約定 source 未配線で行が来ない 相対契約分）は
// 業務的に「取引なし = 0」を表す。0 埋めで列を出し、"-"（cell.empty）にしない
// （生成済み内訳は取引なしコマを 0 とする規約。calc.ts rowToSlots と同方針）。
const emptySlots = (): SlotValue[] => Array<SlotValue>(48).fill(0)

/**
 * previousSlots 用の合算: 片方が undefined（前回提出なし）なら他方をそのまま使い、
 * 両方定義済みの時だけ mergeSlots で対称合算する（両方 undefined なら undefined のまま）。
 */
function mergePreviousSlots(
  a: SlotValue[] | undefined,
  b: SlotValue[] | undefined,
): SlotValue[] | undefined {
  if (a === undefined) return b
  if (b === undefined) return a
  return mergeSlots(a, b)
}

/**
 * 表示する内訳列を組み立てる純関数。
 *
 * 列はすべて値行（= 計画作成時に選択した需要調達計画マスタの取引先から worker が
 * 生成した記録）から導出する。常時固定表示する列は無い:
 *
 * 1. JEPX 市場列（先頭・canonical 順）: 値行に対応 sourceType がある時だけ出す。
 *    調達 JEPXスポットは構造的に 相対契約分 / 調達先未定分 の 2 列に分割（resolveColumnKey
 *    が取引先有無でルーティング。相対契約分は JEPX 約定 source 配線まで空のまま）。
 * 2. 取引先別内訳列（非 JEPX 行のみ。同一列キーは 1 度だけ、マスタ codes[] 順 = 行順）。
 *    JEPX 行は resolveColumnKey で市場列キーに集約済みなので取引先列には現れない。
 *
 * 純関数（slotsMap / prevMap と i18n 解決関数 `translate` を注入）なので単体テスト可能。
 */
export function buildBreakdownColumns(params: {
  rows: BreakdownSourceRow[]
  side: ColumnSide
  slotsMap: Map<string, SlotValue[]>
  prevMap: Map<string, SlotValue[]>
  /** 前回提出データがあるか（差分フラグ算出に使う） */
  hasPrev: boolean
  /** i18n キー → 文言。呼び出し側の useTranslation().t を渡す */
  translate: (key: string) => string
  /**
   * JEPX 市場列を組み立てない。種別に代表事業者以外を選んでいる時に true
   * (procurement-plan-detail.md AC-PPD-026: JEPX は代表事業者に全量を計上する)。
   */
  excludeJepxMarket?: boolean
}): BreakdownColumn[] {
  const { rows, side, slotsMap, prevMap, hasPrev, translate } = params
  const excludeJepxMarket = params.excludeJepxMarket ?? false

  // 前回スロット値。前回提出が無い時は列全体を undefined にする。
  // 前回提出はあるが対応列が無い列（後から足した取引先・契約）は前回値 0 とする
  // （AC-PLC-013: 列が無いことと 0 は計画上同じ意味）。差分フラグ（diffFor）と
  // hover tooltip の前回値は、どちらもこの値から作る。
  const previousSlotsFor = (key: string): SlotValue[] | undefined =>
    hasPrev ? (prevMap.get(key) ?? emptySlots()) : undefined

  const diffFor = (
    slots: SlotValue[],
    previousSlots: SlotValue[] | undefined,
  ): boolean[] =>
    previousSlots === undefined
      ? Array<boolean>(48).fill(false)
      : calcColumnDiff(slots, previousSlots)

  const withPrevious = (
    slots: SlotValue[],
    key: string,
  ): Pick<BreakdownColumn, 'diffFlags' | 'previousSlots'> => {
    const previousSlots = previousSlotsFor(key)
    return { diffFlags: diffFor(slots, previousSlots), previousSlots }
  }

  const cols: BreakdownColumn[] = []

  // 1) JEPX 市場列: 値行に該当 sourceType がある時だけ（= マスタに JEPX 取引先がある時だけ）
  //    canonical 順で先頭に出す。常時固定表示はしない。
  const presentSourceTypes = new Set(rows.map((r) => r.sourceType))
  for (const spec of JEPX_MARKET_SPECS[side]) {
    if (excludeJepxMarket || !presentSourceTypes.has(spec.sourceType)) continue
    const slots = slotsMap.get(spec.key) ?? emptySlots()
    cols.push({
      key: spec.key,
      counterpartyName: undefined,
      counterpartyCode: undefined,
      sourceContractId: undefined,
      sourceLabel: '',
      fixedLabel: {
        code: spec.code,
        primary: translate(spec.primaryKey),
        secondary:
          spec.secondaryKey !== undefined
            ? translate(spec.secondaryKey)
            : undefined,
      },
      slots,
      ...withPrevious(slots, spec.key),
    })
  }

  // 2) 取引先別内訳列（非 JEPX 行のみ。同一列キーは 1 度だけ）
  const seen = new Set<string>()
  for (const row of rows) {
    const key = resolveColumnKey(row, side)
    if (isJepxMarketKey(key, side)) continue
    if (seen.has(key)) continue
    seen.add(key)
    const slots = slotsMap.get(key) ?? emptySlots()
    const sourceLabelKey =
      PLAN_SOURCE_TYPE_I18N_KEYS[row.sourceType] ??
      'page:procurementPlanDetail.sourceType.unspecified'
    cols.push({
      key,
      counterpartyName: row.counterpartyName,
      counterpartyCode: row.counterpartyCode,
      sourceContractId: row.sourceContractId,
      sourceLabel: translate(sourceLabelKey),
      slots,
      ...withPrevious(slots, key),
    })
  }
  return cols
}

/**
 * 「取引先毎合計」表示モード用に、同一取引先 (counterpartyCode) の複数契約列を
 * 1 列へ合算する (AC-12)。取引先を持たない列 (JEPX 市場列。counterpartyCode が
 * undefined) は合算対象外でそのまま通す。
 *
 * 合算後の列は代表として最初に出現した列の counterpartyName / sourceLabel /
 * fixedLabel を引き継ぐ (同一取引先なら値は同一のはず)。sourceContractId は複数
 * 契約を束ねた結果、単一契約を代表しなくなるため undefined にする (= 列同定用の
 * 値が意味を失う)。slots は mergeSlots (= 未入力同士は未入力、どちらかが数値なら
 * 数値同士を加算) で合算する。previousSlots は前回提出がある時は常に定義済み
 * (前回に無かった列は buildBreakdownColumns が前回値 0 を入れる。AC-PLC-013) なので
 * mergeSlots で対称合算する。前回提出が無い時は両方 undefined のまま。片方だけ
 * undefined の場合は防御的に他方をそのまま使う。diffFlags は合算後の previousSlots が
 * 定義済みなら合算後の slots と再比較して算出する (plan-diff-cell-tooltip 設計書
 * Decision 3 および design/plan/submission-detail.html L1520 「変化は表示値どうしで
 * 判定する」: 新規契約が加わっても合算値が前回と同値なら差分なし = 色 + 差分 0 の
 * 矛盾 tooltip を出さない)。合算後の previousSlots が undefined (前回提出なし) の
 * 場合のみ、input diffFlags の OR を保存する (全 false のまま)。
 *
 * 呼び出し元 (index.tsx) が調達合計・販売合計・需給バランスをこの関数の適用
 * *前* の列から算出しているため、本関数はそれらに影響しない (表示ビューの
 * 切替のみ、設計書 §4 主要意思決定)。変更コマ数 (提出確認ダイアログ) は表示モードに
 * 依らず本関数の適用後の列で数える (ユーザー決定 2026-10-14)。
 */
export function aggregateColumnsByCounterparty(
  columns: BreakdownColumn[],
): BreakdownColumn[] {
  const merged: BreakdownColumn[] = []
  const indexByCounterparty = new Map<string, number>()
  for (const col of columns) {
    if (col.counterpartyCode === undefined) {
      merged.push(col)
      continue
    }
    const existingIndex = indexByCounterparty.get(col.counterpartyCode)
    if (existingIndex === undefined) {
      indexByCounterparty.set(col.counterpartyCode, merged.length)
      merged.push({ ...col, sourceContractId: undefined })
      continue
    }
    const existing = merged[existingIndex]
    if (existing === undefined) continue
    const mergedSlots = mergeSlots(existing.slots, col.slots)
    const mergedPreviousSlots = mergePreviousSlots(
      existing.previousSlots,
      col.previousSlots,
    )
    merged[existingIndex] = {
      ...existing,
      slots: mergedSlots,
      previousSlots: mergedPreviousSlots,
      diffFlags:
        mergedPreviousSlots === undefined
          ? existing.diffFlags.map((f, i) => f || (col.diffFlags[i] ?? false))
          : mergedSlots.map((v, i) => isDiffCell(v, mergedPreviousSlots[i])),
    }
  }
  return merged
}

/**
 * 種別で選んだ事業者の内訳で JEPX 市場列を隠すか (procurement-plan-detail.md AC-PPD-026:
 * 合同 BG の計画では JEPX を代表事業者に全量計上し、代表事業者以外では JEPX 市場列を
 * 出さない)。単独 BG (内訳が 1 件以下) では隠さない。代表事業者かどうかは保存済みの
 * is_representative で判定する (XML 取込で代表事業者が M22 に無い計画では、M22 の
 * 事業者はどれも代表事業者以外の構成員として確定している)。
 */
export function shouldExcludeJepxMarket(
  breakdowns: ReadonlyArray<{
    retailerCode: string
    isRepresentative: boolean
  }>,
  selectedRetailerCode: string,
): boolean {
  if (!isJointBg(breakdowns.length)) return false
  const selected = breakdowns.find(
    (b) => b.retailerCode === selectedRetailerCode,
  )
  return !(selected?.isRepresentative ?? false)
}
